import { IRulerInfo } from '@/editor/entities/2d/rulerModel';
import { globalStores } from '../..';
import { genLineSegHash } from '@/editor/utils/hash';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { EFaceSide, IWallEdge } from '@/editor/types';
import { getArcWallRulerInfos } from '@/editor/utils/ruler/wall';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { MAX_CHORD_LENGTH, MAX_SAGITTA } from '@/editor/constant/wall';
import { isArcSeparateWallData } from '@/editor/data/separateWall/utils';
import { SeparateWallData } from '@/editor/data/separateWall/SeparateWall';

type IWallRulerInfo = Omit<IRulerInfo, 'editable'> & {
  wallId?: string;
  sepId: string;
  editable: boolean;
  startMovable?: boolean; // 起始点可移动
  endMovable?: boolean; // 终点可移动
  arcMidMovable?: boolean; // 圆弧中点可移动
};

export function getRelativeWallRulerInfos(mainSeplId: string): IWallRulerInfo[] {
  if (!mainSeplId) return [];
  const mainSwd = globalStores.separateWallStore.getSepData(mainSeplId, false);
  if (!mainSwd) return [];

  const rulerInfos: IWallRulerInfo[] = [];

  // 隔墙关联信息
  const relativeWallInfos = globalStores.separateWallStore.getConnectedWallInfo(mainSeplId, false);
  const startPointConnected = relativeWallInfos
    ? [...relativeWallInfos.mainStartConnectToPoint, ...relativeWallInfos.mainStartOnWallInternal]
    : [];
  const endPointConnected = relativeWallInfos
    ? [...relativeWallInfos.mainEndConnectToPoint, ...relativeWallInfos.mainEndOnWallInternal]
    : [];
  const startMovable = startPointConnected.length === 0;
  const endMovable = endPointConnected.length === 0;
  const editable = startMovable || endMovable;

  // 墙体自身标尺处理
  if (isArcSeparateWallData(mainSwd)) {
    const { sagittaRuler, chordRuler } = getArcWallRulerInfos({
      start: mainSwd.start,
      end: mainSwd.end,
      center: mainSwd.center,
      clockwise: mainSwd.clockwise
    });

    rulerInfos.push(
      { sepId: mainSwd.id, editable: true, arcMidMovable: true, max: MAX_SAGITTA, ...sagittaRuler },
      { sepId: mainSwd.id, editable, startMovable, endMovable, max: MAX_CHORD_LENGTH, ...chordRuler }
    );
  } else {
    rulerInfos.push({
      id: 'mainRuler',
      start: mainSwd.start,
      end: mainSwd.end,
      sepId: mainSwd.id,
      editable,
      startMovable,
      endMovable
    });
  }

  // 查找相连边界：遍历分区闭环取主隔墙的相邻边（含房间内轮廓的 base 隔墙边）
  const isMainStraight = !isArcSeparateWallData(mainSwd);
  const addedRulerHash = new Set<number>();
  // 碎屑边判据的长度基准：优先取主隔墙自身厚度，零厚度（虚拟隔墙、base 隔墙）时退回系统参数
  const debrisThreshold = mainSwd.thickness || globalStores.paramStore.separateWallThickness;

  const addRelativeRulerInfo = (id: string, edge: IWallEdge, options?: Partial<IWallRulerInfo>) => {
    // 零厚度虚拟隔墙两侧边重合、分隔两个分区的边在两个环中各出现一次，需要去重
    const rulerHash = genLineSegHash(edge.start, edge.end);
    if (addedRulerHash.has(rulerHash)) return;

    addedRulerHash.add(rulerHash);
    rulerInfos.push({ id, start: edge.start, end: edge.end, sepId: edge.sepId ?? '', editable: true, ...options });
  };

  const loops = globalStores.regionStore
    .getRoomRegions(mainSwd.relateRoomId)
    .flatMap((region) => [region.pathInfos, ...region.holeInfos]);

  loops.forEach((edges, index) => {
    for (let i = 0; i < edges.length; i++) {
      const edge = edges[i];

      if (edge.sepId !== mainSeplId || !isMainSideEdge(edge)) {
        continue;
      }

      const prevIndex = (i - 1 + edges.length) % edges.length;
      const nextIndex = (i + 1) % edges.length;
      const prevEdge = edges[prevIndex];
      const nextEdge = edges[nextIndex];

      // 圆弧两端连接同一面墙（弦墙、或两端搭在同一条隔墙上）时，环上只有这两条边，前后邻边是同一条。
      // 该邻边与弧自身的弦长标尺方向一致、数值只差墙厚，属于重复标注，不再生成；
      // 也不能落到下面的前后邻边分支，否则同一条边会被当作前、后邻边各生成一次。
      if (prevEdge === nextEdge) {
        break;
      }

      if (
        isRulerableEdge(nextEdge, mainSeplId) &&
        !isDebrisEdge(edges, nextIndex, debrisThreshold) &&
        !(isMainStraight && isParallel(mainSwd, nextEdge))
      ) {
        addRelativeRulerInfo(`${index}-${i}-n`, nextEdge, { startMovable: true });
      }

      if (
        isRulerableEdge(prevEdge, mainSeplId) &&
        !isDebrisEdge(edges, prevIndex, debrisThreshold) &&
        !(isMainStraight && isParallel(mainSwd, prevEdge))
      ) {
        addRelativeRulerInfo(`${index}-${i}-p`, prevEdge, { endMovable: true });
      }
    }
  });

  return rulerInfos;
}

function isMainSideEdge(edge: IWallEdge) {
  return edge.sepSide === EFaceSide.MainLeftSide || edge.sepSide === EFaceSide.MainRightSide;
}

// 相邻边可出标尺：非主隔墙自身的边、直线段、隔墙侧边或 base 隔墙（房间内轮廓）边，排除端盖
function isRulerableEdge(edge: IWallEdge, mainSepId: string) {
  return edge.sepId !== mainSepId && !edge.center && (isMainSideEdge(edge) || edge.sepSide === EFaceSide.Single);
}

/**
 * 弧形隔墙与相邻隔墙相接时，接头处会在相邻隔墙的侧边上残留一小段「碎屑边」：
 * 弧的端盖被相邻隔墙的偏移边裁平，而弧的偏移圆与该偏移边的交点比相邻隔墙的端盖角点浅
 * 半墙厚在偏移圆上的矢高（半径 1656、厚 70 时仅 0.36mm），渲染后四舍五入显示为 0。
 * 它并非真实可标注尺寸，判据与 `utils/ruler/collect.ts` 中分区标尺的碎屑边过滤保持一致。
 */
function isDebrisEdge(edges: IWallEdge[], index: number, thickness: number) {
  const edge = edges[index];
  if (Point2dUtils.distanceTo(edge.start, edge.end) >= thickness) return false;

  // 仅当这段短边在环上紧邻弧形边时才判为碎屑，避免误伤直墙之间真实的小尺寸
  const prevEdge = edges[(index - 1 + edges.length) % edges.length];
  const nextEdge = edges[(index + 1) % edges.length];
  return Boolean(prevEdge.center || nextEdge.center);
}

// 与直的主隔墙平行的相邻边标尺无法通过垂直平移主隔墙修改，需要过滤
function isParallel(mainSwd: SeparateWallData, edge: IWallEdge) {
  return new LineSeg2d(mainSwd.start, mainSwd.end).isParallel(new LineSeg2d(edge.start, edge.end));
}
